import React, { useState, useRef, useEffect } from 'react'
import { Workflow } from 'lucide-react'
import ActivitySidebar from './ActivitySidebar'
import StepSettingsModal from './StepSettingsModal'
import AdvancedIfSettings from './AdvancedIfSettings'
import AdvancedWhileSettings from './AdvancedWhileSettings'
import SaveWorkflowModal from './SaveWorkflowModal'
import VariableManager from './VariableManager'
import { useDispatch, useSelector } from 'react-redux'
import { createWorkflow, getListActivities, getWorkflowDetail, updateWorkflow } from 'slices/thunk'
import { DynamicActivityDefinition, Input, Output, Variable } from '../types'
import { CreateWorkflowPayload, ACTIVITY_TYPES, getOrderedActivitiesFromLinks } from '../helpers'
import WorkflowSpace from './WorkflowSpace'
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom'

interface DesignWorkflowProps {
  isEditing?: boolean;
  workflowId: string;
  onChangeAction: () => void;
}

const DesignWorkflow: React.FC<DesignWorkflowProps> = ({ isEditing, onChangeAction, workflowId }) => {
  const [workflowActivities, setWorkflowActivities] = useState<DynamicActivityDefinition[]>([])
  const [workflowVariables, setWorkflowVariables] = useState<Variable[]>([])
  const [workflowOutputs, setWorkflowOutputs] = useState<Output[]>([])
  const [workflowInputs, setWorkflowInputs] = useState<Input[]>([])
  const [selectedActivity, setSelectedActivity] = useState<DynamicActivityDefinition & { index: number } | null>(null)
  const [showSettings, setShowSettings] = useState(false)
  const [showAdvancedIfSettings, setShowAdvancedIfSettings] = useState(false)
  const [showAdvancedWhileSettings, setShowAdvancedWhileSettings] = useState(false)
  const [showSaveModal, setShowSaveModal] = useState(false)
  const [saveAction, setSaveAction] = useState<'draft' | 'final'>('draft')
  const [isSaving, setIsSaving] = useState(false)
  const [viewMode, setViewMode] = useState<'diagram' | 'list'>('diagram')
  const [customActivities, setCustomActivities] = useState<any[]>([])
  const dropZoneRef = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch<any>();
  const activities = useSelector((state: any) => state.Workflow.activities);
  const [workflowLinks, setWorkflowLinks] = useState<{ from: string; to: string }[]>([])
  const navigate = useNavigate()


  const workflow = useSelector((state: any) => state.Workflow.workflowDetail);

  useEffect(() => {
    dispatch(getListActivities());
  }, [])

  useEffect(() => {
    if (workflowId !== '' && workflowId !== undefined && isEditing) {
      dispatch(getWorkflowDetail(workflowId));
    }
  }, [workflowId])

  useEffect(() => {
    if (workflow?.id && isEditing) {
      setWorkflowActivities(workflow.steps);
      setWorkflowVariables(workflow.variables || []);
      setWorkflowLinks(workflow.links || []);
    }
  }, [workflow, isEditing]);

  useEffect(() => {
    if (activities && activities.length > 0) {
      setCustomActivities(activities);
    }
  }, [activities])

  const addActivityToWorkflow = (activity: any, position?: { x: number; y: number }) => {
    const nodeId = uuidv4();
    const currentIndex = workflowActivities.length;

    const newActivity: DynamicActivityDefinition = {
      id: nodeId,
      label: nodeId,
      nodeId: nodeId,
      type: activity.type,
      name: activity.title,
      description: activity.description,
      parameters: {},
      position: position || { x: currentIndex * 100, y: 100 }
    }

    setWorkflowActivities([...workflowActivities, newActivity]);
  }

  const getActivityDescription = (activity: DynamicActivityDefinition): string => {
    switch (activity.type) {
      case ACTIVITY_TYPES.WRITE_LINE:
        return activity.parameters?.message
          ? `"${activity.parameters.message.substring(0, 30)}${activity.parameters.message.length > 10 ? '...' : ''}"`
          : 'Write message to output'
      case ACTIVITY_TYPES.READ_LINE:
        return activity.parameters?.prompt
          ? `Read: "${activity.parameters.prompt.substring(0, 30)}${activity.parameters.prompt.length > 30 ? '...' : ''}"`
          : 'Read user input'
      case ACTIVITY_TYPES.DELAY:
        return activity.parameters?.duration
          ? `Wait for ${activity.parameters.duration}`
          : 'Wait for specified time'
      case ACTIVITY_TYPES.SEND_EMAIL_MARKETING:
        return activity.parameters?.recipientEmail
          ? `Send email to ${activity.parameters.recipientEmail}`
          : 'Send marketing email'
      case ACTIVITY_TYPES.SEND_MESSAGE_REMINDER:
        return 'Send reminder message'
      case ACTIVITY_TYPES.IF:
        return activity.parameters?.condition
          ? `If condition: ${activity.parameters.condition}`
          : 'Conditional logic branch'
      case ACTIVITY_TYPES.WHILE:
        return activity.parameters?.condition
          ? `While condition: ${activity.parameters.condition}`
          : 'Loop with condition'
      case ACTIVITY_TYPES.FLOW_SEND_HTTP_REQUEST:
        return activity.parameters?.title
          ? `${activity.parameters.title}`
          : 'Send HTTP request'
      default:
        return 'Activity configuration'
    }
  }

  const removeActivity = (activity: DynamicActivityDefinition, index: number) => {
    setWorkflowActivities(workflowActivities.filter((_, i) => i !== index))
  }

  const reorderActivities = (dragIndex: number, hoverIndex: number) => {
    if (dragIndex === hoverIndex || dragIndex < 0 || hoverIndex < 0) return

    const newActivities = [...workflowActivities]
    const draggedActivity = newActivities[dragIndex]

    if (!draggedActivity) return

    newActivities.splice(dragIndex, 1)

    const adjustedHoverIndex = hoverIndex > dragIndex ? hoverIndex - 1 : hoverIndex

    if (adjustedHoverIndex >= newActivities.length) {
      newActivities.push(draggedActivity)
    } else {
      newActivities.splice(adjustedHoverIndex, 0, draggedActivity)
    }

    setWorkflowActivities(newActivities)
  }

  const editActivity = (index: number) => {
    const activity = workflowActivities[index]
    if (!activity) return
    setSelectedActivity({ ...activity, index })

    // Use advanced settings for If and While activities
    if (activity.type === ACTIVITY_TYPES.IF) {
      setShowAdvancedIfSettings(true)
    } else if (activity.type === ACTIVITY_TYPES.WHILE) {
      setShowAdvancedWhileSettings(true)
    } else {
      setShowSettings(true)
    }
  }

  const saveActivitySettings = (parameters: any) => {
    if (selectedActivity) {
      const updatedActivities = [...workflowActivities]
      updatedActivities[selectedActivity.index] = {
        ...updatedActivities[selectedActivity.index],
        parameters
      }
      setWorkflowActivities(updatedActivities)

      setShowSettings(false)
      setSelectedActivity(null)
    }
  }

  const saveAdvancedIfSettings = (parameters: any, variables: Variable[]) => {
    if (selectedActivity) {
      const updatedActivities = [...workflowActivities]
      updatedActivities[selectedActivity.index] = {
        ...updatedActivities[selectedActivity.index],
        parameters
      }
      setWorkflowActivities(updatedActivities)
      setShowAdvancedIfSettings(false)
      setSelectedActivity(null)
    }
  }

  const saveAdvancedWhileSettings = (parameters: any, variables: Variable[]) => {
    if (selectedActivity) {
      const updatedActivities = [...workflowActivities]
      updatedActivities[selectedActivity.index] = {
        ...updatedActivities[selectedActivity.index],
        parameters
      }
      setWorkflowActivities(updatedActivities)
      setShowAdvancedWhileSettings(false)
      setSelectedActivity(null)
    }
  }

  const saveWorkflow = async (name: string, description: string) => {
    setIsSaving(true)
    // Use topological order if links exist
    let orderedActivities = workflowActivities;
    if (workflowLinks.length > 0 && workflowActivities.length > 0) {
      // Create nodes structure from workflowActivities for getOrderedActivitiesFromLinks
      const nodes = workflowActivities.map(activity => ({
        id: activity.nodeId || activity.id || '',
        activity
      }));
      orderedActivities = getOrderedActivitiesFromLinks(nodes, workflowLinks)
    }
    const workflowRequest = CreateWorkflowPayload(name, description, orderedActivities, workflowVariables, workflowOutputs, workflowInputs, workflowLinks)

    // Simulate 3-second save delay
    if (isEditing) {
      await dispatch(updateWorkflow({ id: workflow?.id, ...workflowRequest }))
      await new Promise(resolve => setTimeout(resolve, 3000))
      setWorkflowActivities([])
      navigate(`/workflow`)
      setShowSaveModal(false)
      setIsSaving(false)
    } else {
      await dispatch(createWorkflow(workflowRequest))
      await new Promise(resolve => setTimeout(resolve, 3000))
      setWorkflowActivities([])
      navigate(`/workflow`)
      setShowSaveModal(false)
      setIsSaving(false)
    }

  }

  const handleLinksChange = (links: { from: string; to: string }[], nodes: any[]) => {
    // Shallow compare to prevent infinite update loop
    const linksChanged =
      links.length !== workflowLinks.length ||
      links.some((l, i) => l.from !== workflowLinks[i]?.from || l.to !== workflowLinks[i]?.to);

    if (linksChanged) setWorkflowLinks(links);
  }


  // Update activity position when node moves
  const handleActivityMove = (nodeId: string, position: { x: number; y: number }) => {
    setWorkflowActivities(prevActivities =>
      prevActivities.map(activity => {
        const activityNodeId = `node-${activity.id}`;
        if (activityNodeId === nodeId || activity.nodeId === nodeId) {
          return { ...activity, position };
        }
        return activity;
      })
    );
  };

  return (
    <div className="flex h-full">
      {/* Activities Sidebar */}
      <ActivitySidebar customActivities={customActivities} onAddToWorkflow={addActivityToWorkflow} />

      {/* Workflow Designer */}
      <div className="flex-1 flex flex-col">
        <div className="flex justify-between items-center p-6 pb-[10px] border-b border-slate-200 dark:border-zink-600">
          <div className="flex items-center gap-4">
            <h3 className="text-base text-slate-800 dark:text-zink-50">Workflow Designer</h3>

            {/* View Mode Toggle */}
            <div className="flex bg-slate-100 dark:bg-zink-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode('diagram')}
                className={`px-3 py-1 rounded-md text-sm transition-colors flex items-center gap-2 ${viewMode === 'diagram'
                  ? 'bg-white text-slate-800 shadow-sm dark:bg-zink-600 dark:text-zink-50'
                  : 'text-slate-600 hover:text-slate-800 dark:text-zink-300 dark:hover:text-zink-50'
                  }`}
              >
                <Workflow className="h-4 w-4" />
                Diagram
              </button>
            </div>
          </div>

          {/* Save Buttons */}

          {(<>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setSaveAction('final')
                  setShowSaveModal(true)
                }}
                disabled={isSaving}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${isSaving
                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  : 'bg-custom-500 text-white hover:bg-custom-600'
                  }`}
              >
                {/* <Save className="h-4 w-4" /> */}
                Save Workflow
              </button>
            </div>
          </>)}
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex overflow-hidden">
          {/* Drop Zone */}
          <div className="flex-1 p-6 h-full">
            <div
              ref={dropZoneRef}
              className="h-full border-2 border-dashed bg-white border-slate-300 rounded-lg dark:border-zink-500 overflow-hidden"
            >
              <WorkflowSpace
                activities={workflowActivities}
                links={workflowLinks}
                onEdit={editActivity}
                onRemove={removeActivity}
                onActivityDrop={(activity, position) => {
                  addActivityToWorkflow(activity, position)
                }}
                onLinksChange={handleLinksChange}
                onActivityMove={handleActivityMove}

              />
            </div>
          </div>
          {/* Variables Sidebar */}
          <div className="w-[350px] border-l border-slate-200 dark:border-zink-600 bg-white dark:bg-zink-800 overflow-hidden">
            <div className="h-full overflow-y-auto">
              <VariableManager
                variables={workflowVariables}
                outputs={workflowOutputs}
                inputs={workflowInputs}
                onVariablesChange={setWorkflowVariables}
                onOutputChange={setWorkflowOutputs}
                onInputChange={setWorkflowInputs}
                className="p-4"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <StepSettingsModal
        workflowVariables={workflowVariables}
        workflowOutputs={workflowOutputs}
        selectedStep={selectedActivity ? {
          type: selectedActivity.type,
          title: selectedActivity.name || selectedActivity.type,
          settings: selectedActivity.parameters || {}
        } : null}
        showSettings={showSettings}
        onSave={saveActivitySettings}
        onCancel={() => {
          setShowSettings(false)
          setSelectedActivity(null)
        }}
      />

      <AdvancedIfSettings
        selectedStep={selectedActivity ? {
          type: selectedActivity.type,
          title: selectedActivity.name || selectedActivity.type,
          settings: selectedActivity.parameters || {}
        } : null}
        showSettings={showAdvancedIfSettings}
        onSave={saveAdvancedIfSettings}
        onCancel={() => {
          setShowAdvancedIfSettings(false)
          setSelectedActivity(null)
        }}
        workflowVariables={workflowVariables}
      />

      <AdvancedWhileSettings
        selectedStep={selectedActivity ? {
          type: selectedActivity.type,
          title: selectedActivity.name || selectedActivity.type,
          settings: selectedActivity.parameters || {}
        } : null}
        showSettings={showAdvancedWhileSettings}
        onSave={saveAdvancedWhileSettings}
        onCancel={() => {
          setShowAdvancedWhileSettings(false)
          setSelectedActivity(null)
        }}
        workflowVariables={workflowVariables}
      />

      <SaveWorkflowModal
        showSaveModal={showSaveModal}
        saveAction={saveAction}
        isSaving={isSaving}
        isEditing={isEditing}
        workflowName={workflow?.name}
        workflowDescription={workflow?.description}
        onSave={saveWorkflow}
        onCancel={() => setShowSaveModal(false)}
      />
    </div>
  )
}

export default DesignWorkflow
